import { useState, useCallback, useEffect, useRef } from 'react';
import { ShieldCheck, CheckCircle, XCircle, Copy, Check, Terminal } from 'lucide-react';
import type { ToolComponentProps } from './index';

interface AcceptableCA {
  dn: string;
  cn: string | null;
  o: string | null;
  ou: string | null;
  c: string | null;
}

interface MTLSResult {
  host: string;
  port: number;
  mtlsEnabled: boolean;
  acceptableCAs: AcceptableCA[];
  totalCAs: number;
  serverRequestsClientCert: boolean;
  error: string | null;
  rawCASection?: string;
}

// In production, nginx serves both frontend and API from same origin
const API_BASE = '';

export function MTLSChecker({ initialValue }: ToolComponentProps) {
  const [input, setInput] = useState(initialValue || '');
  const [result, setResult] = useState<MTLSResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [copiedCAs, setCopiedCAs] = useState(false);
  const [copiedCmd, setCopiedCmd] = useState(false);
  const hasAutoTriggered = useRef(false);

  const handleCheck = useCallback(async () => {
    let host = input.trim();
    if (!host) {
      setError('Please enter a hostname');
      return;
    }

    // Strip protocol if provided
    host = host.replace(/^https?:\/\//, '').split('/')[0].split(':')[0];

    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await fetch(`${API_BASE}/api/mtls/${encodeURIComponent(host)}`);
      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'mTLS scan failed');
        return;
      }

      setResult(data);

      // Update browser URL for sharing
      const params = new URLSearchParams();
      params.set('host', host);
      window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
    } catch (err) {
      setError('Failed to connect to the server. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [input]);

  // Auto-trigger check when initialValue is provided via URL
  useEffect(() => {
    if (initialValue && !hasAutoTriggered.current) {
      hasAutoTriggered.current = true;
      handleCheck();
    }
  }, [initialValue, handleCheck]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCheck();
    }
  };

  const handleCopyAllCAs = useCallback(async () => {
    if (!result) return;
    const allCAs = result.acceptableCAs.map(ca => ca.dn).join('\n');
    try {
      await navigator.clipboard.writeText(allCAs);
      setCopiedCAs(true);
      setTimeout(() => setCopiedCAs(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [result]);

  const handleCopyCommand = useCallback(async () => {
    if (!result) return;
    const cmd = `openssl s_client -connect ${result.host}:${result.port} -servername ${result.host}`;
    try {
      await navigator.clipboard.writeText(cmd);
      setCopiedCmd(true);
      setTimeout(() => setCopiedCmd(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [result]);

  const renderOutput = () => {
    if (error) {
      return (
        <div className="text-red-400 p-4 bg-red-500/10 rounded-lg">
          <span className="font-semibold">Error:</span> {error}
        </div>
      );
    }

    if (!result) {
      return (
        <div className="text-[var(--text-muted)] text-center py-8">
          Enter a hostname above and click "Check mTLS CAs" to scan for acceptable client certificate CAs.
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Status Summary */}
        <div className={`flex items-center gap-4 p-4 rounded-lg ${result.mtlsEnabled ? 'bg-[var(--accent-dim)]' : 'bg-[var(--bg-secondary)]'}`}>
          {result.mtlsEnabled ? (
            <CheckCircle className="w-10 h-10 text-[var(--accent)]" />
          ) : (
            <XCircle className="w-10 h-10 text-[var(--text-muted)]" />
          )}
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <span className={`font-semibold text-lg ${result.mtlsEnabled ? 'text-[var(--accent)]' : 'text-[var(--text-muted)]'}`}>
                {result.mtlsEnabled 
                  ? 'mTLS Configured - Client Certificate Required' 
                  : 'mTLS Not Configured'}
              </span>
            </div>
            <div className="text-sm text-[var(--text-muted)] mt-1">
              {result.host}:{result.port}
              {result.mtlsEnabled && (
                <span className="ml-3">
                  • {result.totalCAs} Acceptable CA{result.totalCAs !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* OpenSSL Command */}
        {result.mtlsEnabled && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[var(--text-primary)] font-semibold flex items-center gap-2">
                <Terminal className="w-4 h-4" />
                OpenSSL Command
              </h3>
              <button
                onClick={handleCopyCommand}
                className="flex items-center gap-1 px-3 py-1 text-sm rounded bg-[var(--bg-tertiary)] hover:bg-[var(--accent-dim)] text-[var(--text-muted)] hover:text-[var(--accent)] transition-colors"
              >
                {copiedCmd ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                {copiedCmd ? 'Copied' : 'Copy'}
              </button>
            </div>
            <div className="p-3 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-color)] overflow-x-auto">
              <code className="text-sm text-[var(--text-muted)] font-mono">
                openssl s_client -connect {result.host}:{result.port} -servername {result.host}
              </code>
            </div>
          </div>
        )}

        {/* Acceptable CAs List */}
        {result.mtlsEnabled && result.acceptableCAs.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[var(--accent)] font-semibold flex items-center gap-2">
                <ShieldCheck className="w-4 h-4" />
                Acceptable Client Certificate CAs
              </h3>
              <button
                onClick={handleCopyAllCAs}
                className="flex items-center gap-1 px-3 py-1 text-sm rounded bg-[var(--bg-tertiary)] hover:bg-[var(--accent-dim)] text-[var(--text-muted)] hover:text-[var(--accent)] transition-colors"
              >
                {copiedCAs ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                {copiedCAs ? 'Copied' : 'Copy All'}
              </button>
            </div>
            <div className="p-3 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)] overflow-x-auto">
              <div className="space-y-1 font-mono text-sm">
                {result.acceptableCAs.map((ca, index) => (
                  <div key={index} className="text-[var(--text-primary)] flex">
                    <span className="text-[var(--accent)] w-8 shrink-0">#{index + 1}</span>
                    <span>{ca.dn}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* No mTLS configured */}
        {!result.mtlsEnabled && (
          <div className="p-4 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)]">
            <h3 className="text-[var(--text-primary)] font-semibold mb-2">No Client Certificate Requirements</h3>
            <p className="text-sm text-[var(--text-muted)]">
              This server does not request or require client certificates for mutual TLS authentication.
              Standard one-way TLS (server authentication only) is in use.
            </p>
          </div>
        )}

        {/* mTLS enabled but no CAs parsed - show debug info */}
        {result.mtlsEnabled && result.acceptableCAs.length === 0 && result.rawCASection && (
          <div className="p-4 rounded-lg bg-orange-500/10 border border-orange-500/30">
            <h3 className="text-orange-500 dark:text-orange-400 font-semibold mb-2">Parsing Issue - Raw CA Data</h3>
            <p className="text-sm text-[var(--text-muted)] mb-3">
              The server requires client certificates but we couldn't parse the CA list. Raw data below:
            </p>
            <pre className="p-3 rounded bg-[var(--bg-primary)] text-xs text-[var(--text-muted)] overflow-x-auto whitespace-pre-wrap font-mono">
              {result.rawCASection}
            </pre>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6" onKeyDown={handleKeyDown}>
      {/* Input Section */}
      <div className="space-y-2">
        <label className="block text-xs sm:text-sm font-medium text-[var(--text-primary)]">
          Hostname
        </label>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <input
            type="text"
            className="flex-1 min-w-0 px-3 sm:px-4 py-2 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent font-mono text-sm"
            placeholder="example.com"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            spellCheck={false}
          />
          <button
            type="button"
            onClick={handleCheck}
            disabled={isLoading || !input.trim()}
            className="btn-primary flex items-center justify-center gap-2 px-4 sm:px-6 whitespace-nowrap"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Scanning...
              </>
            ) : (
              <>
                <ShieldCheck className="w-4 h-4" />
                Check mTLS CAs
              </>
            )}
          </button>
        </div>
      </div>

      {/* Output Section */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-[var(--text-primary)]">
          mTLS Configuration
        </label>
        <div className="output-panel">
          {renderOutput()}
        </div>
      </div>
    </div>
  );
}
